import { z } from 'zod'

import { addressSchema, dbEntitySchema, listQueryZodFields } from './system'
import {
  CustomerActionType,
  CustomerFlag,
  CustomerSort,
} from '../constants/customer'
import { AdmissionGroupType } from '../constants/admissionGroup'
import { customerTagBaseSchema } from './customerTag'

export const customerFlagsSchema = z.object({
  regular: z.boolean().nullish(),
  friendsWithOwner: z.boolean().nullish(),
  bigSpender: z.boolean().nullish(),
  talent: z.boolean().nullish(),
  celebrity: z.boolean().nullish(),
  athlete: z.boolean().nullish(),
  influencer: z.boolean().nullish(),
  member: z.boolean().nullish(),
  promoter: z.boolean().nullish(),
  warning: z.boolean().nullish(),
  blocked: z.boolean().nullish(),
  eightySix: z.boolean().nullish(),
})

export const customerTagsSchema = z.array(customerTagBaseSchema)

export const customerReceiptSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  date: z.coerce.date().nullish(),
  description: z.string().nullish(),
  amount: z.number().min(0),
  orderType: z.nativeEnum(AdmissionGroupType).default(AdmissionGroupType.TABLE),
  countAsVisit: z.boolean().default(false),
})

export const customerReceiptCreateArgsSchema = customerReceiptSchema.omit({
  id: true,
})

export const customerReceiptUpdateByIdArgsSchema =
  customerReceiptCreateArgsSchema.partial()

export const customerSchema = dbEntitySchema.extend({
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  organization: z.string(),
  venues: z.array(z.string()).nullish(),
  //
  customerTags: z.array(customerTagBaseSchema).nullish(),
  fraudRiskScore: z.number().nullish(),
  /**
   * @deprecated use customerTags instead
   */
  tags: z.array(z.string()).nullish(),
  /**
   * @deprecated use customerTags instead
   */
  flags: z
    .object({
      regular: z.boolean().nullish(),
      friendsWithOwner: z.boolean().nullish(),
      bigSpender: z.boolean().nullish(),
      talent: z.boolean().nullish(),
      celebrity: z.boolean().nullish(),
      athlete: z.boolean().nullish(),
      influencer: z.boolean().nullish(),
      member: z.boolean().nullish(),
      promoter: z.boolean().nullish(),
      warning: z.boolean().nullish(),
      blocked: z.boolean().nullish(),
      eightySix: z.boolean().nullish(),
    })
    .optional()
    .nullable(),
  /**
   * @deprecated use customerTags instead
   */
  vip: z.boolean().nullish(),
  //
  user: z.string().nullish(),
  prospect: z.string().nullish(),
  gender: z.string().nullish(),
  birthdayDay: z.number().nullish(),
  birthdayMonth: z.number().nullish(),
  birthdayYear: z.number().nullish(),
  birthday: z.date().nullish(),
  profileImage: z.string().nullish(),
  notes: z.string().nullish(),
  //address fields - deprecated
  address: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  administrativeArea: z.string().nullish(),
  addressNew: addressSchema.nullish(),
  //
  country: z.string().nullish(),
  ref: z.string().nullish(),
  attributionId: z.string().nullish(),
  company: z.string().nullish(),
  title: z.string().nullish(),
  employer: z.string().nullish(),
  nationality: z.string().nullish(),
  social: z
    .object({
      facebook: z.string().nullish(),
      twitter: z.string().nullish(),
      instagram: z.string().nullish(),
      linkedin: z.string().nullish(),
      youtube: z.string().nullish(),
      vimeo: z.string().nullish(),
      gravatar: z.string().nullish(),
      klout: z.string().nullish(),
      angellist: z.string().nullish(),
      google: z.string().nullish(),
    })
    .optional()
    .nullable(),
  contacts: z
    .array(
      z.object({
        customer: z.string(),
        admissionGroup: z.string().nullish(),
      })
    )
    .nullish(),
  receipts: z.array(customerReceiptSchema).nullish(),
  marketingOptIn: z.boolean().nullish(),
  marketingOptInDate: z.date().nullish(),
  marketingOptInSignature: z.string().nullish(),
  stats: z
    .object({
      totalSpend: z.number().nullish(),
      totalGuestListSpend: z.number().nullish(),
      averageGuestListSpend: z.number().nullish(),
      totalVisits: z.number().nullish(),
      totalTableVisits: z.number().nullish(),
      totalGuestlistVisits: z.number().nullish(),
      totalTicketVisits: z.number().nullish(),
      firstVisit: z.date().nullish(),
      firstReservation: z.date().nullish(),
      firstGuestlist: z.date().nullish(),
      firstTicket: z.date().nullish(),
      firstTally: z.date().nullish(),
      firstAdmission: z.date().nullish(),
      latestVisit: z.date().nullish(),
      latestReservation: z.date().nullish(),
      latestGuestlist: z.date().nullish(),
      latestTicket: z.date().nullish(),
      latestTally: z.date().nullish(),
      latestAdmission: z.date().nullish(),
      averageMinimum: z.number().nullish(),
      averageTableMinimum: z.number().nullish(),
      averageTicketMinimum: z.number().nullish(),
      averageGuestlistMinimum: z.number().nullish(),
      averageSpend: z.number().nullish(),
      averageTableSpend: z.number().nullish(),
      averageTicketSpend: z.number().nullish(),
      averageGuestlistSpend: z.number().nullish(),
      totalCompedBottles: z.number().nullish(),
      totalCompedTickets: z.number().nullish(),
      totalTableSpend: z.number().nullish(),
      totalTicketSpend: z.number().nullish(),
      totalGuestlistSpend: z.number().nullish(),
    })
    .optional()
    .nullable(),
  zip: z.string().nullish(),
  postalCode: z.string().nullish(),
  attributionType: z.string().nullish(),
  attributionName: z.string().nullish(),
  //import
  importId: z.string().nullish(),
  importName: z.string().nullish(),
  importVenue: z.string().nullish(),
  //integrations
  mailchimp: z
    .object({
      id: z.string().nullish(),
    })
    .optional()
    .nullable(),
  aiq: z
    .object({
      contactId: z.string().nullish(),
    })
    .optional()
    .nullable(),
})

const queryBoolean = z.preprocess((value) => {
  if (typeof value === 'string') {
    const v = value.toLowerCase()
    if (v === 'true' || v === '1') return true
    if (v === 'false' || v === '0') return false
  }
  return value
}, z.boolean())

export const customerListQuerySchema = z.object({
  ...listQueryZodFields,
  sort: z.nativeEnum(CustomerSort).nullish(),
  organizationId: z.string(),
  venueId: z.array(z.string()).or(z.string()).nullish(),
  customerId: z.array(z.string()).or(z.string()).nullish(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  gender: z.string().nullish(),
  birthdayMonth: z.array(z.coerce.number()).or(z.coerce.number()).nullish(),
  search: z.string().nullish(),
  customerTagId: z.array(z.string()).or(z.string()).nullish(),
  customerTagValue: z.array(z.string()).or(z.string()).nullish(),
  totalSpendMin: z.coerce.number().nullish(),
  totalSpendMax: z.coerce.number().nullish(),
  totalVisitsMin: z.coerce.number().nullish(),
  totalVisitsMax: z.coerce.number().nullish(),
  totalTableVisitsMin: z.coerce.number().nullish(),
  totalTableVisitsMax: z.coerce.number().nullish(),
  totalTicketVisitsMin: z.coerce.number().nullish(),
  totalTicketVisitsMax: z.coerce.number().nullish(),
  averageMinimumMin: z.coerce.number().nullish(),
  averageMinimumMax: z.coerce.number().nullish(),
  hasPhone: queryBoolean.nullish(),
  hasEmail: queryBoolean.nullish(),
  firstVisitStart: z.coerce.date().nullish(),
  firstVisitEnd: z.coerce.date().nullish(),
  lastVisitStart: z.coerce.date().nullish(),
  lastVisitEnd: z.coerce.date().nullish(),
  addedOnStart: z.coerce.date().nullish(),
  addedOnEnd: z.coerce.date().nullish(),
  modifiedStart: z.coerce.date().nullish(),
  modifiedEnd: z.coerce.date().nullish(),
  marketingOptIn: queryBoolean.nullish(),
  hasNoPhone: queryBoolean.nullish(),
  hasNoEmail: queryBoolean.nullish(),
})

export const customerCreateArgsSchema = customerSchema
  .omit({
    id: true,
    _id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venues: true,
    customerTags: true,
    tags: true,
    receipts: true,
  })
  .extend({
    organizationId: z.string(),
    customerTagIds: z.array(z.string()).nullish(),
    userId: z.string().nullish(),
    prospectId: z.string().nullish(),
    venueId: z.string().nullish(),
    tags: z.array(z.string()).or(z.string()).nullish(),
  })

export const customerUpdateByIdArgsSchema = customerSchema
  .omit({
    id: true,
    _id: true,
    deleted: true,
    created: true,
    modified: true,
    organization: true,
    venues: true,
    customerTags: true,
    tags: true,
    stats: true,
    receipts: true,
  })
  .extend({
    customerTagIds: z.array(z.string()).nullish(),
    userId: z.string().nullish(),
    prospectId: z.string().nullish(),
    venueId: z.array(z.string()).or(z.string()).nullish(),
    venueIds: z.array(z.string()).or(z.string()).nullish(),
    tags: z.array(z.string()).or(z.string()).nullish(),
  })

export const customerListResponseSchema = z.object({
  data: z.array(customerSchema),
})

export const customerMergeActionSchema = customerUpdateByIdArgsSchema
  .omit({
    user: true,
    prospect: true,
  })
  .extend({
    fromIds: z.array(z.string()),
    type: z.nativeEnum(CustomerActionType).optional(),
  })

export const customerContactEntrySchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  profileImage: z.string().nullish(),
  admissionGroup: z.string().nullish(),
})

export const customerContactsResponseSchema = z.object({
  data: z.array(customerContactEntrySchema),
})
